/* eslint-disable @typescript-eslint/no-use-before-define */
import { Button } from '@dropbox/dig-components/buttons';
import { useBreakpoint } from '@dropbox/dig-components/hooks';
import { Menu } from '@dropbox/dig-components/menu';
import { Text } from '@dropbox/dig-components/typography';
import classnames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  useZoomContext,
  useZoomContextFromPageContainer,
  zoomContext,
} from 'signer-app';
import styles from 'signer-app/parts/toolbar-zoom-controls/toolbar-zoom-controls.module.css';
import { FIT_WIDTH } from 'signer-app/signature-request';
import { useWhiteLabelSignerAppTextColorFixEnabled } from 'signer-app/white-labeling/use-white-label-signer-app-text-color-fix';
import { WhiteLabelThemeProvider } from 'signer-app/white-labeling/theme-provider';

export const ZoomDropdown = (props: { className?: string }) => {
  const intl = useIntl();
  const { zoom, setZoom, fitWidthScale } = useZoomContext();
  const applyWhiteLabelTextColorFix =
    useWhiteLabelSignerAppTextColorFixEnabled();

  const { small: displaySmall } = useBreakpoint();

  const defaultZoomOptions = useMemo(
    () => ({
      0.25: intl.formatNumber(0.25, { style: 'percent' }),
      0.5: intl.formatNumber(0.5, { style: 'percent' }),
      0.75: intl.formatNumber(0.75, { style: 'percent' }),
      1: intl.formatNumber(1, { style: 'percent' }),
      1.25: intl.formatNumber(1.25, { style: 'percent' }),
      1.5: intl.formatNumber(1.5, { style: 'percent' }),
      1.75: intl.formatNumber(1.75, { style: 'percent' }),
      2: intl.formatNumber(2, { style: 'percent' }),
      2.25: intl.formatNumber(2.25, { style: 'percent' }),
      2.5: intl.formatNumber(2.5, { style: 'percent' }),
      2.75: intl.formatNumber(2.75, { style: 'percent' }),
      3: intl.formatNumber(3, { style: 'percent' }),
    }),
    [intl],
  );

  const handleChangeZoom = useCallback(
    (value) => setZoom(value === fitWidthScale ? FIT_WIDTH : value),
    [fitWidthScale, setZoom],
  );

  const zoomOptions: { [s: number]: string } = {
    ...defaultZoomOptions,
    [zoom]: intl.formatNumber(Number(Math.round(zoom * 100).toFixed(0)) / 100, {
      style: 'percent',
    }),
  };

  const zoomKeys = Object.keys(zoomOptions)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <Menu.Wrapper onSelection={handleChangeZoom}>
      {({ getTriggerProps, getContentProps }) => (
        <>
          <Button
            {...getTriggerProps()}
            id="zoom-menu-button"
            className={classnames(
              props.className,
              applyWhiteLabelTextColorFix && styles.zoomMenuWhiteLabel,
            )}
            data-testid="zoom"
            variant="transparent"
            hasNoUnderline
            withDropdownIcon
          >
            <Text
              size={displaySmall ? 'small' : 'xsmall'}
              color={applyWhiteLabelTextColorFix ? 'subtle' : 'standard'}
            >
              <FormattedMessage
                id="af12761bad39c375ea995ca45b03e0a2fb66c8a71cc4733818e9873ae46f6b7a"
                description="The title of the zoom dropdown list button responsible for controlling the zoom level (e.g. 'Zoom: Fit Width', 'Zoom: 200%', etc.)."
                defaultMessage={'{zoom}'}
                values={{
                  zoom: zoomOptions[zoom],
                }}
              />
            </Text>
          </Button>
          <WhiteLabelThemeProvider colors="reset">
            <Menu.Content
              {...getContentProps()}
              aria-labelledby="zoom-menu-button"
            >
              {zoomKeys.map((value) => (
                <Menu.ActionItem key={value} value={value}>
                  {zoomOptions[value]}
                </Menu.ActionItem>
              ))}
            </Menu.Content>
          </WhiteLabelThemeProvider>
        </>
      )}
    </Menu.Wrapper>
  );
};

export default function ToolbarZoomControls() {
  const zoomContextValue = useZoomContextFromPageContainer();

  return (
    <zoomContext.Provider value={zoomContextValue}>
      {zoomContextValue != null && <ZoomDropdown className={styles.zoomMenu} />}
    </zoomContext.Provider>
  );
}
